import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/**
 * CursorTracker — gold ring + cinematic radial spotlight that follows the cursor.
 * Hidden on touch devices. Spotlight is throttled with rAF and CSS variables.
 */
const CursorTracker = () => {
  const [enabled, setEnabled] = useState(false);
  const [hovered, setHovered] = useState(false);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 350, damping: 28, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 350, damping: 28, mass: 0.4 });
  const spotRef = useRef(null);
  const rafRef = useRef(0);
  const lastPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const mql = window.matchMedia("(pointer: fine)");
    setEnabled(mql.matches);
    const onChange = (e) => setEnabled(e.matches);
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      if (spotRef.current) {
        spotRef.current.style.setProperty("--mx", `${lastPos.current.x}px`);
        spotRef.current.style.setProperty("--my", `${lastPos.current.y}px`);
      }
      rafRef.current = 0;
    };
    const onMove = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      lastPos.current = { x: e.clientX, y: e.clientY };
      if (!rafRef.current) rafRef.current = requestAnimationFrame(tick);
    };
    const onOver = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const isInteractive =
        tag === "a" ||
        tag === "button" ||
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        e.target?.closest?.("a, button, [role='button']");
      setHovered(!!isInteractive);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseover", onOver);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, x, y]);

  if (!enabled) return null;

  return (
    <>
      {/* Cinematic radial spotlight that follows the cursor */}
      <div
        ref={spotRef}
        className="cursor-spotlight"
        aria-hidden="true"
        data-testid="cursor-spotlight"
      />

      {/* Gold ring */}
      <motion.div
        style={{
          x: sx,
          y: sy,
          translateX: "-50%",
          translateY: "-50%",
        }}
        animate={{ scale: hovered ? 1.6 : 1, opacity: hovered ? 0.95 : 0.75 }}
        transition={{ duration: 0.18 }}
        className="pointer-events-none fixed top-0 left-0 z-[90] rounded-full"
        data-testid="cursor-ring"
      >
        <div
          className="rounded-full border"
          style={{
            width: 28,
            height: 28,
            borderColor: "rgba(224,192,104,0.9)",
            boxShadow: "0 0 14px rgba(224,192,104,0.55)",
            backdropFilter: "blur(2px)",
          }}
        />
      </motion.div>
    </>
  );
};

export default CursorTracker;
